import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiResponse } from './data.service';

export interface RagAskResponse {
  answer?: string;
  model?: string;
  message?: string;
  response?: string;
  result?: string;
  citations?: RagCitation[];
  sources?: any;
  used_note_ids?: string[];
}

export interface RagCitation {
  rank?: number;
  note_id?: string;
  id?: string;
  title?: string;
  snippet?: string;
  status?: string;
  last_modified?: string;
}

export interface RagAskRequest {
  question: string;
  top_k?: number;
  temperature?: number;
  max_context_chars?: number;
}

export type NotesAskMode = 'all_notes' | 'filtered_notes' | 'single_note';

export interface NotesAskInlineNote {
  _id?: string;
  Title?: string;
  DescriptionHtml?: string;
  CommentsHtml?: string;
}

export interface NotesAskSource {
  mode: NotesAskMode;
  note_ids?: string[];
  note_id?: string;
  note?: NotesAskInlineNote;
}

export interface NotesAskRetrieval {
  top_k: number;
  prefer_recent: boolean;
}

export interface NotesAskGeneration {
  temperature: number;
  max_context_chars: number;
}

export interface NotesAskOptions {
  debug: boolean;
}

export interface NotesAskRequestV2 {
  question: string;
  source: NotesAskSource;
  retrieval: NotesAskRetrieval;
  generation: NotesAskGeneration;
  options: NotesAskOptions;
}

export interface AskCurrentNotePayload {
  Description: string;
  Comments: string;
}

export interface AskCurrentNoteRequest {
  question: string;
  note: AskCurrentNotePayload;
}

export interface AskCurrentNoteResponse {
  answer?: string;
  source?: string;
  model?: string;
  generated_at?: string;
}

export interface NotesAskResponse {
  answer?: string;
  model?: string;
  source?: string;
  generated_at?: string;
  message?: string;
  response?: string;
  result?: string;
  citations?: RagCitation[];
  cited_note_ids?: string[];
  sources?: any;
  data?: any;
  error?: any;
  status?: string;
}

export interface RagReindexStatus {
  running?: boolean;
  started_at?: string;
  finished_at?: string;
  last_result?: string;
  last_error?: string;
}

export interface AiAskRequest {
  question: string;
  reference_date?: string;
  use_tools?: boolean;
  include_debug?: boolean;
  max_tool_calls?: number;
}

export interface AiSource {
  note_id?: string;
  title?: string;
  status?: string;
  reference_date?: string;
  date?: string;
  score?: number;
  snippet?: string;
}

export interface AiToolCallDebug {
  tool_name?: string;
  arguments?: any;
  result_count?: number;
  latency_ms?: number;
}

export interface AiAskDebugInfo {
  model?: string;
  tool_call_count?: number;
  executed_tools?: AiToolCallDebug[];
  latency_ms?: number;
  tokens_used?: number;
  token_usage?: any;
  request_id?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  [key: string]: any;
}

export interface AiAskResponse {
  answer?: string;
  sources?: AiSource[];
  tool_calls?: any[];
  debug?: AiAskDebugInfo;
  request_id?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RagService {
  private readonly baseUrl = '/api/rag';
  private readonly askNotesUrl = '/api/notes/ask';
  private readonly aiAskUrl = '/api/ai/ask';

  constructor(private http: HttpClient) {}

  reindex(): Observable<ApiResponse<{ message?: string }>> {
    return this.http.post<ApiResponse<{ message?: string }>>(`${this.baseUrl}/reindex`, {});
  }

  askNotes(payload: NotesAskRequestV2): Observable<NotesAskResponse> {
    return this.http.post<NotesAskResponse>(this.askNotesUrl, payload);
  }

  askNotesAi(request: AiAskRequest): Observable<AiAskResponse> {
    return this.http.post<AiAskResponse>(this.aiAskUrl, request);
  }

  indexNote(noteId: string): Observable<ApiResponse<{ message?: string }>> {
    return this.http.post<ApiResponse<{ message?: string }>>(`${this.baseUrl}/reindex/${encodeURIComponent(noteId)}`, null);
  }

  reindexStatus(): Observable<ApiResponse<RagReindexStatus>> {
    return this.http.get<ApiResponse<RagReindexStatus>>(`${this.baseUrl}/reindex/status`);
  }
}
